import { SEOHead } from "@/components/SEOHead";
import { Reveal } from "@/components/Reveal";
import { IMAGES, DIRECTORS, SERVICE_GROUPS, VISI, MISI } from "@/data/company";

const GALLERY = [
  { src: IMAGES.officeBuilding, alt: "Gedung kantor modern — lingkup konstruksi gedung perkantoran PT. Luminara Global Persada", cls: "row-span-2" },
  { src: IMAGES.construction, alt: "Aktivitas proyek konstruksi dengan crane — kegiatan lapangan PT. Luminara Global Persada", cls: "" },
  { src: IMAGES.architectsBlueprint, alt: "Tim manajemen meninjau gambar blueprint proyek arsitektur", cls: "" },
  { src: IMAGES.luxuryInterior, alt: "Interior premium hasil pekerjaan finishing dan desain interior", cls: "row-span-2" },
  { src: IMAGES.engineersOutdoor, alt: "Insinyur dan pengawas lapangan berdiskusi di lokasi proyek konstruksi", cls: "" },
  { src: IMAGES.blueprint, alt: "Blueprint perencanaan teknis konstruksi bangunan", cls: "" },
];

const DIVISIONS = [
  {
    label: "Divisi Operasional",
    director: { role: "Direktur Operasional", name: "Risky Setya Hadi" },
    staff: [
      { role: "Kepala Pengawas Lapangan", name: "M. Rizky Fiqi" },
      { role: "Staff Operasional", name: "Elvan Julio Maarebia" },
      { role: "Staff Operasional", name: "Adamsyah" },
    ],
  },
  {
    label: "Divisi Marketing",
    director: { role: "Direktur Marketing", name: "Rifqi Rivaldo" },
    staff: [{ role: "Staff Marketing", name: "M. Mulya Gaffari" }],
  },
  {
    label: "Divisi Keuangan",
    director: { role: "Direktur Keuangan", name: "Iir Yusnita" },
    staff: [
      { role: "Staff Admin Keuangan", name: "Siti Shafira" },
      { role: "Staff Admin Keuangan", name: "Marsha Haniyah" },
    ],
  },
];

const TIM_TEKNIS = [
  { name: "M. Rizky Fiqi", role: "Kepala Pengawas Lapangan", photo: "/assets/direksi/m-rizky-fiqi.jpg", lead: true },
  { name: "Elvan Julio Maarebia", role: "Staff Operasional", photo: "/assets/tim/elvan-julio-maarebia.jpg" },
  { name: "Adamsyah", role: "Staff Operasional", photo: "/assets/tim/adamsyah.jpg" },
];

const TIM_UMUM = [
  { name: "Rifqi Rivaldo", role: "Direktur Marketing", photo: "/assets/direksi/rifqi-rivaldo.jpg", lead: true },
  { name: "M. Mulya Gaffari", role: "Staff Marketing", photo: "/assets/tim/m-mulya-gaffari.jpg" },
];

const OrgNode = ({ role, name, sub = null, primary = false }) => (
  <div
    className={`px-6 py-4 border text-center ${
      primary ? "border-gold bg-gold/10" : "border-white/15 bg-navy-900"
    }`}
  >
    <p className={`text-xs tracking-widest uppercase ${primary ? "text-gold" : "text-slate-400"}`}>{role}</p>
    {sub && <p className="text-[10px] text-slate-500 italic mt-0.5">({sub})</p>}
    <p className="mt-1 font-heading font-semibold text-white text-sm md:text-base">{name}</p>
  </div>
);

export default function About() {
  return (
    <>
      <SEOHead
        title="Tentang Perusahaan | PT. Luminara Global Persada"
        description="Profil PT. Luminara Global Persada: perusahaan konstruksi, perdagangan, arsitektur, interior, dan lanskap di Depok — visi, misi, dewan direksi, dan struktur organisasi."
      />

      <section className="pt-40 pb-20 bg-navy-900 border-b border-white/10" data-testid="about-header">
        <div className="container-site">
          <Reveal>
            <p className="kicker mb-4">Tentang Perusahaan</p>
            <h1 className="font-heading font-semibold text-4xl md:text-5xl tracking-tight max-w-3xl">
              Profil PT. Luminara Global Persada
            </h1>
            <p className="mt-6 max-w-2xl text-slate-400 leading-relaxed">
              Mengenal lebih dekat perusahaan, nilai yang kami pegang, serta jajaran yang menjalankan
              setiap amanah pekerjaan.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="section-pad" data-testid="company-profile-section">
        <div className="container-site grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <p className="kicker mb-4">Siapa Kami</p>
            <h2 className="font-heading font-semibold text-3xl md:text-4xl tracking-tight">
              Perusahaan dengan orientasi kualitas, ketepatan waktu, dan integritas
            </h2>
            <div className="mt-6 space-y-5 text-slate-400 leading-relaxed">
              <p>
                <strong className="text-white font-semibold">PT. Luminara Global Persada</strong> adalah
                perusahaan yang bergerak di bidang konstruksi, perdagangan, arsitektur, interior, dan
                lanskap. Kami melayani kebutuhan pembangunan gedung, pekerjaan sipil dan infrastruktur,
                penyelesaian interior, pengadaan barang, hingga perancangan arsitektur dan penataan lanskap.
              </p>
              <p>
                Sebagai perusahaan yang berorientasi pada kualitas, setiap pekerjaan di PT. Luminara
                Global Persada direncanakan secara cermat, dilaksanakan dengan pengawasan mutu berlapis,
                dan diselesaikan tepat waktu. Kami menjunjung tinggi integritas dan transparansi dalam
                setiap hubungan kerja sama dengan klien, mitra, maupun pemasok.
              </p>
              <p>
                Dengan cakupan usaha yang luas dan saling terhubung, PT. Luminara Global Persada
                menghadirkan solusi terpadu — dari tahap perencanaan desain, pelaksanaan konstruksi,
                hingga dukungan pengadaan — dalam satu koordinasi yang efisien dan akuntabel.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative">
              <img
                src={IMAGES.glassFacade}
                alt="Fasad kaca gedung modern — representasi kualitas pekerjaan konstruksi PT. Luminara Global Persada"
                className="w-full aspect-[3/4] object-cover border border-white/10"
              />
              <div className="absolute -top-6 -left-6 w-40 h-40 border border-gold/40 hidden md:block" aria-hidden="true" />
            </div>
          </Reveal>
        </div>

        <div className="container-site mt-24" data-testid="kbli-list-section">
          <Reveal>
            <p className="kicker mb-4">Cakupan Bidang Usaha</p>
            <h2 className="font-heading font-semibold text-2xl md:text-3xl tracking-tight max-w-2xl">
              19 bidang usaha terdaftar dalam lima kategori layanan
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {SERVICE_GROUPS.flatMap((g) =>
              g.items.map((it) => (
                <div
                  key={it.kbli}
                  data-testid={`kbli-chip-${it.kbli}`}
                  className="flex items-center gap-4 border border-white/10 bg-navy-900 px-5 py-4 transition-colors duration-300 hover:border-gold/40"
                >
                  <span className="font-heading text-gold text-sm font-semibold tracking-wider">{it.kbli}</span>
                  <span className="text-sm text-slate-300">{it.name}</span>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      <section className="section-pad bg-navy-900 border-y border-white/10" data-testid="visi-misi-section">
        <div className="container-site">
          <Reveal>
            <p className="kicker mb-4">Visi &amp; Misi</p>
            <h2 className="font-heading font-semibold text-3xl md:text-4xl tracking-tight max-w-2xl">
              Arah dan komitmen kami
            </h2>
          </Reveal>
          <div className="mt-14 grid md:grid-cols-12 gap-8">
            <Reveal className="md:col-span-5">
              <div data-testid="visi-card" className="h-full border border-gold/40 bg-navy-800 p-10 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-1 h-full bg-gold" aria-hidden="true" />
                <h3 className="font-heading font-semibold text-2xl text-gold">Visi</h3>
                <p className="mt-5 text-lg text-white leading-relaxed font-medium">{VISI}</p>
              </div>
            </Reveal>
            <Reveal className="md:col-span-7" delay={0.15}>
              <div data-testid="misi-card" className="h-full border border-white/10 bg-navy-800 p-10">
                <h3 className="font-heading font-semibold text-2xl text-white">Misi</h3>
                <ol className="mt-6 space-y-4">
                  {MISI.map((m, i) => (
                    <li key={i} className="flex gap-4">
                      <span className="font-heading text-gold font-semibold shrink-0">{String(i + 1).padStart(2, "0")}</span>
                      <span className="text-sm text-slate-300 leading-relaxed">{m}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="section-pad" data-testid="directors-section">
        <div className="container-site">
          <Reveal>
            <p className="kicker mb-4">Dewan Direksi &amp; Jajaran</p>
            <h2 className="font-heading font-semibold text-3xl md:text-4xl tracking-tight max-w-2xl">
              Jajaran pimpinan PT. Luminara Global Persada
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8">
            {DIRECTORS.map((d, i) => (
              <Reveal key={d.role} delay={i * 0.08}>
                <article
                  data-testid={`director-card-${i}`}
                  className="group bg-navy-900 border border-white/10 overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:border-gold/50 h-full"
                >
                  <div className="overflow-hidden">
                    <img
                      src={d.photo}
                      alt={`Foto ${d.name}, ${d.role} PT. Luminara Global Persada`}
                      className="w-full aspect-[3/5] object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]"
                    />
                  </div>
                  <div className="p-5 border-t border-gold/30">
                    <h3 className="sr-only">{d.name} — {d.role}</h3>
                    <p className="text-xs text-slate-400 leading-relaxed">{d.desc}</p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad bg-navy-900 border-y border-white/10" data-testid="org-structure-section">
        <div className="container-site">
          <Reveal className="text-center max-w-2xl mx-auto">
            <p className="kicker mb-4">Struktur Organisasi</p>
            <h2 className="font-heading font-semibold text-3xl md:text-4xl tracking-tight">
              Hierarki Jabatan &amp; Garis Komando
            </h2>
            <p className="mt-5 text-sm text-slate-400 leading-relaxed">
              Struktur organisasi PT. Luminara Global Persada yang jelas dan akuntabel, dari Direktur
              Utama sebagai pimpinan perusahaan hingga staf divisi.
            </p>
          </Reveal>
          <Reveal delay={0.15} className="mt-16 flex flex-col items-center">
            <div data-testid="org-chart" className="flex flex-col items-center w-full max-w-5xl">
              <OrgNode primary role="Direktur Utama" sub="Pimpinan Perusahaan" name="Rasyid Hadi Munandar" />
              <div className="w-0.5 h-10 bg-gold" aria-hidden="true" />

              <div className="relative w-full">
                <div
                  className="hidden md:block absolute top-0 left-[16.667%] right-[16.667%] h-0.5 bg-gold"
                  aria-hidden="true"
                />
                <div className="hidden md:block absolute -top-1 left-[16.667%] -translate-x-1/2 w-2.5 h-2.5 bg-gold rotate-45" aria-hidden="true" />
                <div className="hidden md:block absolute -top-1 left-1/2 -translate-x-1/2 w-2.5 h-2.5 bg-gold rotate-45" aria-hidden="true" />
                <div className="hidden md:block absolute -top-1 left-[83.333%] -translate-x-1/2 w-2.5 h-2.5 bg-gold rotate-45" aria-hidden="true" />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-8 md:pt-0">
                  {DIVISIONS.map((div, i) => (
                    <div key={div.label} data-testid={`org-division-${i}`} className="flex flex-col items-center">
                      <div className="w-0.5 h-10 bg-gold" aria-hidden="true" />
                      <p className="text-[11px] tracking-[0.25em] uppercase text-gold mb-3 mt-2 md:mt-0">
                        {div.label}
                      </p>
                      <OrgNode role={div.director.role} name={div.director.name} />
                      <div className="w-0.5 h-6 bg-gold/60" aria-hidden="true" />
                      <div className="w-full max-w-xs space-y-3">
                        {div.staff.map((s) => (
                          <div key={s.name}>
                            <div className="px-4 py-3 border border-white/10 bg-navy-800">
                              <p className="text-[10px] tracking-widest uppercase text-slate-500">{s.role}</p>
                              <p className="mt-0.5 font-heading font-medium text-white text-sm">{s.name}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="section-pad" data-testid="team-section">
        <div className="container-site">
          <Reveal>
            <p className="kicker mb-4">Tim Kami</p>
            <h2 className="font-heading font-semibold text-3xl md:text-4xl tracking-tight max-w-2xl">
              Tim Divisi Teknis &amp; Tim Divisi Umum
            </h2>
            <p className="mt-5 max-w-2xl text-slate-400 leading-relaxed">
              Tim lapangan dan tim penunjang yang memastikan setiap pekerjaan berjalan sesuai standar perusahaan.
            </p>
          </Reveal>

          {[
            { id: "teknis", title: "Tim Divisi Teknis", members: TIM_TEKNIS },
            { id: "umum", title: "Tim Divisi Umum", members: TIM_UMUM },
          ].map((group, gi) => (
            <div key={group.id} className="mt-16">
              <Reveal>
                <h3
                  data-testid={`team-group-${group.id}`}
                  className="font-heading font-semibold text-xl text-white flex items-center gap-4"
                >
                  <span className="w-8 h-px bg-gold" aria-hidden="true" />
                  {group.title}
                </h3>
              </Reveal>
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
                {group.members.map((m, i) => (
                  <Reveal key={m.name} delay={i * 0.08}>
                    <article
                      data-testid={`team-card-${group.id}-${i}`}
                      className="group bg-navy-900 border border-white/10 overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:border-gold/50"
                    >
                      <div className="overflow-hidden relative">
                        <img
                          src={m.photo}
                          alt={`Foto ${m.name}, ${m.role} ${group.title} PT. Luminara Global Persada`}
                          className="w-full aspect-[3/5] object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]"
                        />
                        {m.lead && (
                          <span className="absolute top-3 right-3 bg-gold text-navy-950 text-[10px] font-bold tracking-widest uppercase px-2.5 py-1">
                            Pembimbing Tim
                          </span>
                        )}
                      </div>
                      <h4 className="sr-only">{m.name} — {m.role}</h4>
                    </article>
                  </Reveal>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="section-pad border-t border-white/10" data-testid="gallery-section">
        <div className="container-site">
          <Reveal>
            <p className="kicker mb-4">Galeri</p>
            <h2 className="font-heading font-semibold text-3xl md:text-4xl tracking-tight max-w-2xl">
              Suasana kerja dan lingkup proyek kami
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-2 md:grid-cols-4 auto-rows-[180px] md:auto-rows-[220px] gap-4">
            {GALLERY.map((g, i) => (
              <Reveal key={i} delay={i * 0.05} className={g.cls}>
                <div data-testid={`gallery-item-${i}`} className="w-full h-full overflow-hidden border border-white/10 group">
                  <img
                    src={g.src}
                    alt={g.alt}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
